import { Controller } from "stimulus"

// #1745: het persoonlijke widgetdashboard. Buiten de bewerkmodus regelt deze
// controller alleen kleine dingen in widgets (tabs, notities automatisch
// opslaan, de tekstwidget, vensters). In de bewerkmodus: slepen via het
// sleepicoon (muis en touch, met pointer events), omhoog/omlaag, formaat,
// instellingen, dupliceren, verwijderen, widgets toevoegen uit het paneel
// (klikken of slepen), ongedaan maken, annuleren en opslaan.
//
// De volgorde in de DOM is de indeling. Opslaan stuurt de lijst naar de
// server, die de rechten per widget opnieuw controleert.
const MATEN = {
  klein: [1, 1],
  middel: [2, 1],
  groot: [2, 2],
  breed: [3, 1],
  balk: [4, 1],
  extra_groot: [3, 2],
  volle_breedte: [4, 2]
}

export default class extends Controller {
  static targets = ["raster", "widget", "plaats", "paneel", "bediening", "instellingenDialoog", "instellingenBody",
    "menu", "bieb", "mijnLijst", "formaatKnop", "voorbeeldNaam", "voorbeeldTekst", "toevoegKnop", "zoek", "ongedaan",
    "leeg"]
  static values = { opslaanUrl: String, widgetUrl: String, teksten: Object }

  connect() {
    this.bewerken = false
    this.geschiedenis = []
    this.gekozen = null
    this.timers = new WeakMap()
    this.sluitMenuBij = (e) => { if (this.hasMenuTarget && !this.menuTarget.contains(e.target)) this.sluitMenu() }
    this.toetsen = (e) => { if (e.key === "Escape") this.sluitMenu() }
    this.waarschuw = (e) => {
      if (this.bewerken && this.gewijzigd()) { e.preventDefault(); e.returnValue = "" }
    }
    // Bewaart een widget zelf zijn instellingen (Fundamental: Instellen), dan
    // neemt de indeling die over, zodat opslaan in de bewerkmodus ze niet terugzet
    this.frameGeladen = (e) => {
      const widget = e.target.closest?.(".dw-widget")
      const bron = e.target.querySelector?.(":scope > [data-dw-instellingen]")
      if (widget && bron && !this.bewerken) widget.dataset.instellingen = bron.dataset.dwInstellingen
    }
    document.addEventListener("keydown", this.toetsen)
    window.addEventListener("beforeunload", this.waarschuw)
    this.element.addEventListener("turbo:frame-load", this.frameGeladen)
  }

  disconnect() {
    document.removeEventListener("keydown", this.toetsen)
    document.removeEventListener("pointerdown", this.sluitMenuBij)
    window.removeEventListener("beforeunload", this.waarschuw)
  }

  t(sleutel) {
    return (this.tekstenValue || {})[sleutel] || sleutel
  }

  // ---------- bewerkmodus ----------

  start() {
    this.bewerken = true
    this.origineel = JSON.stringify(this.indeling())
    this.geschiedenis = []
    this.element.classList.add("is-bewerken")
    // Op een breed scherm staat het paneel meteen rechts; smaller opent het
    // als lade via Widget toevoegen of de lege plek
    if (this.hasPaneelTarget) this.paneelTarget.hidden = !window.matchMedia("(min-width: 1181px)").matches
    this.verversPaneel()
    this.verversOngedaan()
  }

  annuleren() {
    if (!this.gewijzigd()) {
      this.stop()
      return
    }
    this.bewerken = false
    window.location.reload()
  }

  stop() {
    this.bewerken = false
    this.element.classList.remove("is-bewerken")
    if (this.hasPaneelTarget) this.paneelTarget.hidden = true
    this.sluitMenu()
  }

  // Herstellen verlaat de pagina bewust: geen waarschuwing over wijzigingen
  verlaat() {
    this.bewerken = false
  }

  sluitPaneel() {
    if (this.hasPaneelTarget) this.paneelTarget.hidden = true
  }

  openPaneel() {
    if (this.hasPaneelTarget) {
      this.paneelTarget.hidden = false
      this.paneelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  async opslaan(event) {
    const knop = event?.currentTarget
    if (knop) knop.disabled = true
    try {
      const antwoord = await fetch(this.opslaanUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrf() },
        body: JSON.stringify({ widgets: this.indeling() }),
        credentials: "same-origin"
      })
      if (!antwoord.ok) throw new Error(antwoord.status)
      this.bewerken = false
      window.location.reload()
    } catch (e) {
      if (knop) knop.disabled = false
      window.alert(this.t("opslaan_mislukt"))
    }
  }

  gewijzigd() {
    return this.origineel !== undefined && this.origineel !== JSON.stringify(this.indeling())
  }

  widgets() {
    return Array.from(this.rasterTarget.querySelectorAll(":scope > .dw-widget"))
  }

  indeling() {
    return this.widgets().map((el) => ({
      uid: el.dataset.uid,
      type: el.dataset.type,
      formaat: el.dataset.formaat,
      instellingen: this.instellingenVan(el)
    }))
  }

  instellingenVan(el) {
    try { return JSON.parse(el.dataset.instellingen || "{}") } catch (e) { return {} }
  }

  // ---------- ongedaan maken ----------

  registreer() {
    this.geschiedenis.push(this.widgets().map((el) => ({ el, formaat: el.dataset.formaat, instellingen: el.dataset.instellingen })))
    if (this.geschiedenis.length > 50) this.geschiedenis.shift()
    this.verversOngedaan()
  }

  ongedaanMaken() {
    const vorige = this.geschiedenis.pop()
    if (!vorige) return
    this.widgets().forEach((el) => { if (!vorige.some((v) => v.el === el)) el.remove() })
    vorige.forEach(({ el, formaat, instellingen }) => {
      const anders = el.dataset.formaat !== formaat || el.dataset.instellingen !== instellingen
      el.dataset.instellingen = instellingen
      if (el.dataset.formaat !== formaat) this.zetMaat(el, formaat)
      this.rasterTarget.insertBefore(el, this.plaatsTarget)
      if (anders) this.herlaad(el)
    })
    this.na()
  }

  verversOngedaan() {
    if (this.hasOngedaanTarget) this.ongedaanTarget.disabled = this.geschiedenis.length === 0
  }

  na() {
    this.verversOngedaan()
    this.verversPaneel()
    if (this.hasLeegTarget) this.leegTarget.hidden = this.widgets().length > 0
  }

  // ---------- widget aanpassen ----------

  zetMaat(el, formaat) {
    const [b, h] = MATEN[formaat] || MATEN.middel
    el.className = el.className.split(" ").filter((k) => !/^dw-(b|h)\d$|^dw-f-/.test(k)).join(" ")
    el.classList.add(`dw-b${b}`, `dw-h${h}`, `dw-f-${formaat}`)
    el.dataset.formaat = formaat
  }

  url(el, extra = {}) {
    const params = new URLSearchParams({
      type: el.dataset.type,
      uid: el.dataset.uid,
      formaat: el.dataset.formaat,
      instellingen: el.dataset.instellingen || "{}",
      ...extra
    })
    return `${this.widgetUrlValue}?${params.toString()}`
  }

  herlaad(el) {
    const frame = el.querySelector("turbo-frame")
    if (!frame) return
    const nieuw = this.url(el)
    frame.setAttribute("aria-busy", "true")
    if (frame.getAttribute("src") === nieuw && frame.reload) frame.reload()
    else frame.setAttribute("src", nieuw)
  }

  widgetVan(event) {
    return event.currentTarget.closest(".dw-widget") || this.menuWidget
  }

  kiesFormaat(event) {
    const el = this.menuWidget
    const formaat = event.currentTarget.dataset.formaat
    this.sluitMenu()
    if (!el || el.dataset.formaat === formaat) return
    this.registreer()
    this.zetMaat(el, formaat)
    this.herlaad(el)
    this.na()
  }

  verwijder() {
    const el = this.menuWidget
    this.sluitMenu()
    if (!el) return
    if (el.dataset.type === "notities" && !window.confirm(this.t("notities_blijven"))) return
    this.registreer()
    el.remove()
    this.na()
  }

  dupliceer() {
    const el = this.menuWidget
    this.sluitMenu()
    if (!el) return
    this.registreer()
    const kopie = this.nieuweWidget(el.dataset.type, el.dataset.formaat, el.dataset.instellingen, el.dataset.naam,
      el.dataset.formaten, el.dataset.meerdere)
    el.after(kopie)
    this.na()
  }

  omhoog(event) {
    const el = this.widgetVan(event)
    const vorige = el.previousElementSibling
    if (!vorige || !vorige.classList.contains("dw-widget")) return
    this.registreer()
    vorige.before(el)
    this.focusOp(event, el)
    this.na()
  }

  omlaag(event) {
    const el = this.widgetVan(event)
    const volgende = el.nextElementSibling
    if (!volgende || !volgende.classList.contains("dw-widget")) return
    this.registreer()
    volgende.after(el)
    this.focusOp(event, el)
    this.na()
  }

  focusOp(event, el) {
    const soort = event.currentTarget.dataset.action
    const knop = Array.from(el.querySelectorAll(".dw-pijl-knop")).find((k) => k.dataset.action === soort)
    if (knop) knop.focus()
  }

  // ---------- menu (drie puntjes) ----------

  menu(event) {
    event.stopPropagation()
    const knop = event.currentTarget
    const el = knop.closest(".dw-widget")
    if (this.menuWidget === el && !this.menuTarget.hidden) { this.sluitMenu(); return }
    this.menuWidget = el
    let formaten = []
    try { formaten = JSON.parse(el.dataset.formaten || "[]") } catch (e) { formaten = [] }
    const m = this.menuTarget
    m.innerHTML = ""
    const kop = document.createElement("p")
    kop.className = "dw-menu-kop"
    kop.textContent = this.t("widgetgrootte")
    m.appendChild(kop)
    formaten.forEach((f) => {
      m.appendChild(this.menuKnop(`${this.t(`formaat_${f}`)} <small>${MATEN[f].join(" × ")}</small>`, "kiesFormaat",
        { formaat: f }, el.dataset.formaat === f))
    })
    m.appendChild(document.createElement("hr"))
    m.appendChild(this.menuKnop(this.t("instellingen"), "openInstellingen"))
    if (el.dataset.meerdere === "true") m.appendChild(this.menuKnop(this.t("dupliceren"), "dupliceer"))
    m.appendChild(this.menuKnop(this.t("verwijderen"), "verwijder", {}, false, "is-gevaar"))
    m.hidden = false
    const r = knop.getBoundingClientRect()
    const breed = 220
    // Past het menu niet onder de knop, dan klapt het naar boven
    const hoog = m.offsetHeight
    const top = r.bottom + 6 + hoog > window.innerHeight - 8 ? Math.max(8, r.top - hoog - 6) : r.bottom + 6
    m.style.top = `${top}px`
    m.style.left = `${Math.max(8, Math.min(window.innerWidth - breed - 8, r.right - breed))}px`
    setTimeout(() => document.addEventListener("pointerdown", this.sluitMenuBij), 0)
    const eerste = m.querySelector("button")
    if (eerste) eerste.focus()
  }

  menuKnop(html, actie, data = {}, actief = false, klasse = "") {
    const b = document.createElement("button")
    b.type = "button"
    b.setAttribute("role", "menuitem")
    b.className = `dw-menu-item ${actief ? "is-actief" : ""} ${klasse}`
    b.innerHTML = html
    b.dataset.action = `dashboard-bewerken#${actie}`
    Object.entries(data).forEach(([k, v]) => { b.dataset[k] = v })
    return b
  }

  sluitMenu() {
    if (this.hasMenuTarget) this.menuTarget.hidden = true
    document.removeEventListener("pointerdown", this.sluitMenuBij)
  }

  // ---------- instellingen ----------

  openInstellingen() {
    const el = this.menuWidget
    this.sluitMenu()
    if (!el) return
    const sjabloon = this.element.querySelector(`template[data-instellingen-voor="${el.dataset.type}"]`)
    if (!sjabloon) return
    this.instellingenWidget = el
    const body = this.instellingenBodyTarget
    body.innerHTML = ""
    body.appendChild(sjabloon.content.cloneNode(true))
    const waarden = this.instellingenVan(el)
    body.querySelectorAll("[data-instelling]").forEach((veld) => {
      const naam = veld.dataset.instelling
      const waarde = waarden[naam]
      if (veld.dataset.soort === "meerkeuze") {
        const lijst = Array.isArray(waarde) ? waarde : []
        const regels = Array.from(veld.querySelectorAll("[data-waarde]"))
        regels.sort((a, b) => {
          const ia = lijst.indexOf(a.dataset.waarde)
          const ib = lijst.indexOf(b.dataset.waarde)
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
        })
        regels.forEach((r) => {
          veld.appendChild(r)
          r.querySelector("input").checked = lijst.includes(r.dataset.waarde)
        })
      } else if (veld.dataset.soort === "aan_uit") {
        veld.checked = waarde === true
      } else if (waarde !== undefined && waarde !== null) {
        veld.value = waarde
      }
    })
    this.instellingenDialoogTarget.querySelector("[data-dw-dialoog-titel]").textContent = el.dataset.naam
    this.instellingenDialoogTarget.showModal()
  }

  verschuifKeuze(event) {
    const regel = event.currentTarget.closest("[data-waarde]")
    if (event.currentTarget.dataset.richting === "op" && regel.previousElementSibling) {
      regel.previousElementSibling.before(regel)
    } else if (event.currentTarget.dataset.richting === "neer" && regel.nextElementSibling) {
      regel.nextElementSibling.after(regel)
    }
    event.currentTarget.focus()
  }

  pasInstellingenToe(event) {
    event.preventDefault()
    const el = this.instellingenWidget
    if (!el) return
    const waarden = this.instellingenVan(el)
    this.instellingenBodyTarget.querySelectorAll("[data-instelling]").forEach((veld) => {
      const naam = veld.dataset.instelling
      if (veld.dataset.soort === "meerkeuze") {
        waarden[naam] = Array.from(veld.querySelectorAll("[data-waarde]"))
          .filter((r) => r.querySelector("input").checked).map((r) => r.dataset.waarde)
      } else if (veld.dataset.soort === "aan_uit") {
        waarden[naam] = veld.checked
      } else {
        waarden[naam] = veld.value
      }
    })
    this.registreer()
    el.dataset.instellingen = JSON.stringify(waarden)
    this.instellingenDialoogTarget.close()
    this.herlaad(el)
    this.na()
  }

  sluitInstellingen(event) {
    event?.preventDefault()
    this.instellingenDialoogTarget.close()
  }

  // ---------- nieuwe widgets ----------

  nieuweUid() {
    return Math.random().toString(36).slice(2, 12).padEnd(10, "0")
  }

  nieuweWidget(type, formaat, instellingen, naam, formaten, meerdere) {
    const el = document.createElement("div")
    el.className = `dw-widget dw-t-${type} is-nieuw`
    el.id = `dw-${this.nieuweUid()}`
    el.dataset.uid = el.id.slice(3)
    el.dataset.type = type
    el.dataset.instellingen = instellingen || "{}"
    el.dataset.naam = naam || type
    el.dataset.formaten = formaten || "[]"
    el.dataset.meerdere = meerdere || "false"
    el.dataset.dashboardBewerkenTarget = "widget"
    this.zetMaat(el, formaat)
    if (this.hasBedieningTarget) el.appendChild(this.bedieningTarget.content.cloneNode(true))
    const frame = document.createElement("turbo-frame")
    frame.id = `dw-frame-${el.dataset.uid}`
    frame.className = "dw-frame"
    frame.setAttribute("target", "_top")
    frame.innerHTML = `<section class="wk-kaart dw-kaart dw-laden" aria-busy="true"><div class="dw-skelet"></div>` +
      `<div class="dw-skelet is-kort"></div><p class="dw-laden-tekst">${this.t("laden")}</p></section>`
    el.appendChild(frame)
    frame.setAttribute("src", this.url(el))
    setTimeout(() => el.classList.remove("is-nieuw"), 1200)
    return el
  }

  kiesBieb(event) {
    const tegel = event.currentTarget
    if (tegel.disabled) return
    this.gekozen = tegel
    this.biebTarget.querySelectorAll(".dw-bieb-tegel").forEach((t) => {
      t.classList.toggle("is-gekozen", t === tegel)
      t.setAttribute("aria-pressed", t === tegel ? "true" : "false")
    })
    const formaten = JSON.parse(tegel.dataset.formaten)
    this.gekozenFormaat = tegel.dataset.standaard
    this.formaatKnopTargets.forEach((k) => {
      const kan = formaten.includes(k.dataset.formaat)
      k.disabled = !kan
      k.hidden = !kan
      k.classList.toggle("is-gekozen", k.dataset.formaat === this.gekozenFormaat)
    })
    if (this.hasVoorbeeldNaamTarget) this.voorbeeldNaamTarget.textContent = tegel.dataset.naam
    if (this.hasVoorbeeldTekstTarget) this.voorbeeldTekstTarget.textContent = tegel.dataset.omschrijving
    if (this.hasToevoegKnopTarget) this.toevoegKnopTarget.disabled = false
  }

  kiesNieuwFormaat(event) {
    this.gekozenFormaat = event.currentTarget.dataset.formaat
    this.formaatKnopTargets.forEach((k) => k.classList.toggle("is-gekozen", k === event.currentTarget))
  }

  voegToe() {
    if (!this.gekozen || this.gekozen.disabled) return
    this.registreer()
    const el = this.maakUitTegel(this.gekozen, this.gekozenFormaat)
    this.rasterTarget.insertBefore(el, this.plaatsTarget)
    this.na()
    el.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  maakUitTegel(tegel, formaat) {
    return this.nieuweWidget(tegel.dataset.type, formaat || tegel.dataset.standaard, "{}", tegel.dataset.naam,
      tegel.dataset.formaten, tegel.dataset.meerdere)
  }

  zoeken() {
    const term = this.zoekTarget.value.trim().toLowerCase()
    this.biebTarget.querySelectorAll(".dw-bieb-tegel").forEach((t) => {
      const tekst = `${t.dataset.naam} ${t.dataset.omschrijving}`.toLowerCase()
      t.hidden = term !== "" && !tekst.includes(term)
    })
  }

  verversPaneel() {
    if (!this.hasBiebTarget) return
    const aanwezig = new Set(this.widgets().map((el) => el.dataset.type))
    this.biebTarget.querySelectorAll(".dw-bieb-tegel").forEach((t) => {
      const vol = t.dataset.meerdere !== "true" && aanwezig.has(t.dataset.type)
      t.disabled = vol
      t.classList.toggle("is-geplaatst", aanwezig.has(t.dataset.type))
      if (vol && this.gekozen === t) {
        this.gekozen = null
        t.classList.remove("is-gekozen")
        if (this.hasToevoegKnopTarget) this.toevoegKnopTarget.disabled = true
      }
    })
    if (this.hasMijnLijstTarget) {
      const lijst = this.mijnLijstTarget
      lijst.innerHTML = ""
      this.widgets().forEach((el) => {
        const li = document.createElement("li")
        const knop = document.createElement("button")
        knop.type = "button"
        knop.className = "dw-mijn-regel"
        knop.innerHTML = `<span></span><small>${this.t(`formaat_${el.dataset.formaat}`)}</small>`
        knop.querySelector("span").textContent = el.dataset.naam
        knop.addEventListener("click", () => {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
          el.classList.add("is-nieuw")
          setTimeout(() => el.classList.remove("is-nieuw"), 1200)
        })
        li.appendChild(knop)
        lijst.appendChild(li)
      })
    }
  }

  // ---------- slepen ----------

  pakOp(event) {
    if (!this.bewerken || event.button > 0) return
    event.preventDefault()
    const el = event.currentTarget.closest(".dw-widget")
    this.registreer()
    this.sleep({ el, event, nieuw: false })
  }

  // Een tegel uit het paneel: klikken kiest hem, slepen (met de muis) zet hem
  // direct op het raster. Op een touchscherm scrolt het paneel gewoon.
  biebOmlaag(event) {
    if (!this.bewerken || event.pointerType === "touch" || event.button > 0) return
    const tegel = event.currentTarget
    if (tegel.disabled) return
    const startX = event.clientX
    const startY = event.clientY
    const beweeg = (e) => {
      if (Math.abs(e.clientX - startX) + Math.abs(e.clientY - startY) < 8) return
      window.removeEventListener("pointermove", beweeg)
      window.removeEventListener("pointerup", los)
      this.registreer()
      this.sleep({ tegel, event: e, nieuw: true })
    }
    const los = () => {
      window.removeEventListener("pointermove", beweeg)
      window.removeEventListener("pointerup", los)
    }
    window.addEventListener("pointermove", beweeg)
    window.addEventListener("pointerup", los)
  }

  sleep({ el, tegel, event, nieuw }) {
    const plek = document.createElement("div")
    plek.className = "dw-plek"
    let zweef
    let dx = 20
    let dy = 20
    if (nieuw) {
      const [b, h] = MATEN[tegel.dataset.standaard] || MATEN.middel
      plek.classList.add(`dw-b${b}`, `dw-h${h}`)
      zweef = tegel.cloneNode(true)
      zweef.classList.add("dw-zweef-tegel")
      document.body.appendChild(zweef)
    } else {
      const r = el.getBoundingClientRect()
      dx = event.clientX - r.left
      dy = event.clientY - r.top
      el.classList.forEach((k) => { if (/^dw-(b|h)\d$/.test(k)) plek.classList.add(k) })
      el.before(plek)
      zweef = el
      el.classList.add("is-zwevend")
      el.style.width = `${r.width}px`
      el.style.height = `${r.height}px`
    }
    this.element.classList.add("is-slepen")
    const scroller = this.element.closest(".cc-werkvlak") || document.scrollingElement
    const zet = (x, y) => {
      zweef.style.left = `${x - dx}px`
      zweef.style.top = `${y - dy}px`
    }
    zet(event.clientX, event.clientY)

    const beweeg = (e) => {
      e.preventDefault()
      zet(e.clientX, e.clientY)
      const onder = document.elementFromPoint(e.clientX, e.clientY)
      const doel = onder?.closest(".dw-widget:not(.is-zwevend), .dw-plaats, .dw-plek")
      if (doel && this.rasterTarget.contains(doel) && doel !== plek) {
        if (doel.classList.contains("dw-plaats")) {
          doel.before(plek)
        } else {
          const r = doel.getBoundingClientRect()
          const voor = e.clientY < r.top + r.height / 3 || (e.clientY < r.bottom && e.clientX < r.left + r.width / 2)
          if (voor) doel.before(plek)
          else doel.after(plek)
        }
      }
      const sr = scroller.getBoundingClientRect ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight }
      if (e.clientY < Math.max(sr.top, 0) + 60) scroller.scrollBy(0, -18)
      else if (e.clientY > Math.min(sr.bottom, window.innerHeight) - 60) scroller.scrollBy(0, 18)
    }
    const los = () => {
      window.removeEventListener("pointermove", beweeg)
      window.removeEventListener("pointerup", los)
      window.removeEventListener("pointercancel", los)
      this.element.classList.remove("is-slepen")
      if (nieuw) {
        zweef.remove()
        if (plek.isConnected) plek.replaceWith(this.maakUitTegel(tegel))
        else this.geschiedenis.pop()
      } else {
        el.classList.remove("is-zwevend")
        el.style.width = ""
        el.style.height = ""
        el.style.left = ""
        el.style.top = ""
        plek.replaceWith(el)
      }
      if (this.plaatsTarget.nextElementSibling) this.rasterTarget.appendChild(this.plaatsTarget)
      this.na()
    }
    window.addEventListener("pointermove", beweeg, { passive: false })
    window.addEventListener("pointerup", los)
    window.addEventListener("pointercancel", los)
  }

  // ---------- kleine dingen in widgets ----------

  kiesTab(event) {
    const knop = event.currentTarget
    const scope = knop.closest(".dw-kaart, .dw-paneel")
    if (!scope) return
    scope.querySelectorAll("[data-dw-tab]").forEach((t) => {
      const actief = t === knop
      t.classList.toggle("is-actief", actief)
      t.setAttribute("aria-selected", actief ? "true" : "false")
    })
    scope.querySelectorAll("[data-dw-paneel]").forEach((p) => { p.hidden = p.dataset.dwPaneel !== knop.dataset.dwTab })
  }

  verstuurFormulier(event) {
    const form = event.currentTarget.form
    if (form?.requestSubmit) form.requestSubmit()
    else form?.submit()
  }

  openVenster(event) {
    const venster = document.getElementById(event.currentTarget.dataset.venster)
    if (venster?.showModal) venster.showModal()
  }

  sluitVenster(event) {
    event.currentTarget.closest("dialog")?.close()
  }

  stelUit(element, fn) {
    clearTimeout(this.timers.get(element))
    this.timers.set(element, setTimeout(fn, 600))
  }

  async verstuurNaar(url, body) {
    const antwoord = await fetch(url, {
      method: "PATCH",
      headers: { Accept: "application/json", "X-CSRF-Token": this.csrf() },
      body,
      credentials: "same-origin"
    })
    if (!antwoord.ok) throw new Error(antwoord.status)
    return antwoord.json()
  }

  notitieTyp(event) {
    const veld = event.currentTarget
    const tijd = veld.closest(".dw-notitie")?.querySelector("[data-gewijzigd]")
    this.stelUit(veld, async () => {
      const body = new FormData()
      body.append("inhoud", veld.value)
      try {
        const data = await this.verstuurNaar(veld.dataset.url, body)
        if (tijd) tijd.textContent = data.gewijzigd
      } catch (e) {
        if (tijd) tijd.textContent = this.t("niet_opgeslagen")
      }
    })
  }

  tekstBewerken(event) {
    const kaart = event.currentTarget.closest(".dw-kaart")
    const tekst = kaart?.querySelector(".dw-tekst")
    if (!tekst) return
    tekst.querySelector("[data-dw-tekst-weergave]").hidden = true
    const bewerk = tekst.querySelector("[data-dw-tekst-bewerk]")
    bewerk.hidden = false
    bewerk.querySelector("textarea")?.focus()
  }

  tekstTyp(event) {
    const tekst = event.currentTarget.closest(".dw-tekst")
    const tijd = tekst.querySelector("[data-gewijzigd]")
    this.stelUit(tekst, async () => {
      const body = new FormData()
      tekst.querySelectorAll("[data-veld]").forEach((v) => body.append(`instellingen[${v.dataset.veld}]`, v.value))
      try {
        await this.verstuurNaar(tekst.dataset.url, body)
        if (tijd) tijd.textContent = this.t("opgeslagen")
      } catch (e) {
        if (tijd) tijd.textContent = this.t("niet_opgeslagen")
      }
    })
  }

  tekstKlaar(event) {
    const widget = event.currentTarget.closest(".dw-widget")
    const tekst = event.currentTarget.closest(".dw-tekst")
    const nieuwe = {}
    tekst.querySelectorAll("[data-veld]").forEach((v) => { nieuwe[v.dataset.veld] = v.value })
    if (widget) {
      widget.dataset.instellingen = JSON.stringify({ ...this.instellingenVan(widget), ...nieuwe })
      // Eerst de laatste wijziging bewaren, dan de widget opnieuw tekenen
      clearTimeout(this.timers.get(tekst))
      const body = new FormData()
      Object.entries(nieuwe).forEach(([k, v]) => body.append(`instellingen[${k}]`, v))
      this.verstuurNaar(tekst.dataset.url, body).finally(() => this.herlaad(widget))
    }
  }

  csrf() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
